import { usePageTitle } from "@/hooks/use-page-title";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, ChevronDown, ChevronRight, GripVertical, Save, X, Trash2, Clock, Link } from "lucide-react";
import { ResponsiveLayout } from "@/components/layout/responsive-layout";
import { ServiceTemplateFormModal } from "@/components/modals/service-template-form-modal";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { ServiceType, TaskTemplate } from "@shared/schema";

interface ServiceTemplateWithRelations {
  id: number;
  serviceTypeId: string;
  serviceType: ServiceType;
  items: Array<{
    id: number;
    sequenceNumber: number;
    taskTemplate: TaskTemplate;
  }>;
}

export function ServiceTemplates() {
  usePageTitle("Service Templates");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<ServiceTemplateWithRelations | null>(null);
  const [openTemplates, setOpenTemplates] = useState<Record<number, boolean>>({});
  const [editingTemplates, setEditingTemplates] = useState<Record<number, {
    selectedTaskIds: number[];
    hasChanges: boolean;
  }>>({});
  const [draggedTaskId, setDraggedTaskId] = useState<number | null>(null);

  // Fetch service types
  const { data: serviceTypes = [] } = useQuery<ServiceType[]>({
    queryKey: ['/api/service-types']
  });

  // Fetch all task templates for editing
  const { data: allTaskTemplates = [] } = useQuery<TaskTemplate[]>({
    queryKey: ['/api/task-templates'],
    select: (data) => data.filter(t => t.isActive),
  });

  // Fetch service templates
  const { data: templates = [], isLoading } = useQuery<ServiceTemplateWithRelations[]>({
    queryKey: ['/api/service-templates']
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, taskTemplateIds }: { id: number; taskTemplateIds: number[] }) =>
      apiRequest('PUT', `/api/service-templates/${id}`, { taskTemplateIds }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/service-templates'] });
      toast({
        title: "Success",
        description: "Service template updated successfully",
      });
      // Reset editing state to current saved values (not delete, so UI stays in sync)
      const editing = editingTemplates[variables.id];
      if (editing) {
        setEditingTemplates(prev => ({
          ...prev,
          [variables.id]: {
            selectedTaskIds: editing.selectedTaskIds,
            hasChanges: false,
          }
        }));
      }
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update service template",
        variant: "destructive",
      });
    },
  });

  const handleCreate = () => {
    setSelectedTemplate(null);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedTemplate(null);
    setModalOpen(false);
  };

  const toggleTemplate = (templateId: number) => {
    setOpenTemplates(prev => ({
      ...prev,
      [templateId]: !prev[templateId]
    }));

    // Initialize editing state when opening
    if (!openTemplates[templateId] && !editingTemplates[templateId]) {
      const template = templates.find(t => t.id === templateId);
      if (template) {
        setEditingTemplates(prev => ({
          ...prev,
          [templateId]: {
            selectedTaskIds: template.items.map(item => item.taskTemplate.id),
            hasChanges: false,
          }
        }));
      }
    }
  };

  const toggleTaskSelection = (templateId: number, taskId: number) => {
    setEditingTemplates(prev => {
      const current = prev[templateId];
      if (!current) return prev;

      const isSelected = current.selectedTaskIds.includes(taskId);
      const newSelectedIds = isSelected
        ? current.selectedTaskIds.filter(id => id !== taskId)
        : [...current.selectedTaskIds, taskId];

      return {
        ...prev,
        [templateId]: {
          selectedTaskIds: newSelectedIds,
          hasChanges: true,
        }
      };
    });
  };

  const handleDragStart = (taskId: number) => {
    setDraggedTaskId(taskId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (templateId: number, targetTaskId: number) => {
    if (!draggedTaskId || draggedTaskId === targetTaskId) {
      setDraggedTaskId(null);
      return;
    }

    setEditingTemplates(prev => {
      const current = prev[templateId];
      if (!current) return prev;

      const newOrder = [...current.selectedTaskIds];
      const draggedIndex = newOrder.indexOf(draggedTaskId);
      const targetIndex = newOrder.indexOf(targetTaskId);

      newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, draggedTaskId);

      return {
        ...prev,
        [templateId]: {
          selectedTaskIds: newOrder,
          hasChanges: true,
        }
      };
    });

    setDraggedTaskId(null);
  };

  const handleSave = (templateId: number) => {
    const editing = editingTemplates[templateId];
    if (!editing) return;

    updateMutation.mutate({
      id: templateId,
      taskTemplateIds: editing.selectedTaskIds,
    });
  };

  const handleCancel = (templateId: number) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setEditingTemplates(prev => ({
        ...prev,
        [templateId]: {
          selectedTaskIds: template.items.map(item => item.taskTemplate.id),
          hasChanges: false,
        }
      }));
    }
  };

  const serviceTypesWithoutTemplates = serviceTypes.filter(
    st => !templates.some(t => t.serviceTypeId === st.id)
  );

  return (
    <ResponsiveLayout 
      title="Service Task Lists" 
      subtitle="Define standard task sequences for each service type. These auto-populate new work orders."
      currentPage="service-templates"
    >
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-sm text-muted-foreground">
              {templates.length} template{templates.length !== 1 ? 's' : ''} configured
              {serviceTypesWithoutTemplates.length > 0 && ` • ${serviceTypesWithoutTemplates.length} service type${serviceTypesWithoutTemplates.length !== 1 ? 's' : ''} without templates`}
            </p>
          </div>
          <Button onClick={handleCreate} data-testid="button-create-service-template">
            <Plus className="mr-2 h-4 w-4" />
            Add Service Task List
          </Button>
        </div>

        {isLoading ? (
          <div className="text-center py-8">Loading service templates...</div>
        ) : templates.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <p className="text-muted-foreground">No service templates found.</p>
              <p className="text-sm text-muted-foreground mt-2">
                Create service task lists to define standard task sequences for each service type.
              </p>
              <Button onClick={handleCreate} className="mt-4" data-testid="button-create-first-template">
                <Plus className="mr-2 h-4 w-4" />
                Create First Service Task List
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {templates.map((template) => {
              const isOpen = openTemplates[template.id];
              const editing = editingTemplates[template.id];
              const selectedTasks = editing?.selectedTaskIds
                .map(id => allTaskTemplates.find(t => t.id === id))
                .filter(Boolean) as TaskTemplate[] || [];

              return (
                <Collapsible
                  key={template.id}
                  open={isOpen}
                  onOpenChange={() => toggleTemplate(template.id)}
                >
                  <Card data-testid={`card-service-template-${template.id}`}>
                    <CollapsibleTrigger className="w-full">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {isOpen ? (
                              <ChevronDown className="h-5 w-5 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-5 w-5 text-muted-foreground" />
                            )}
                            <div className="text-left">
                              <CardTitle className="text-lg" data-testid={`text-service-type-${template.id}`}>
                                {template.serviceType.name}
                              </CardTitle>
                              <p className="text-sm text-muted-foreground mt-1">
                                {(editing?.hasChanges ? selectedTasks.length : template.items.length)} task{(editing?.hasChanges ? selectedTasks.length : template.items.length) !== 1 ? 's' : ''} in sequence
                              </p>
                            </div>
                          </div>
                          {editing?.hasChanges && (
                            <Badge variant="outline" className="ml-auto mr-2">
                              Unsaved Changes
                            </Badge>
                          )}
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                      <CardContent className="pt-0">
                        {/* Selected tasks in order */}
                        <div className="space-y-3 mb-4">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-medium">Task Sequence</h4>
                            {editing?.hasChanges && (
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCancel(template.id);
                                  }}
                                  data-testid={`button-cancel-${template.id}`}
                                >
                                  <X className="h-4 w-4 mr-1" />
                                  Cancel
                                </Button>
                                <Button
                                  variant="default"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSave(template.id);
                                  }}
                                  disabled={updateMutation.isPending}
                                  data-testid={`button-save-${template.id}`}
                                >
                                  <Save className="h-4 w-4 mr-1" />
                                  Save Changes
                                </Button>
                              </div>
                            )}
                          </div>

                          {selectedTasks.length === 0 ? (
                            <p className="text-sm text-muted-foreground italic">No tasks selected</p>
                          ) : (
                            <div className="space-y-2">
                              {selectedTasks.map((task, index) => (
                                <div
                                  key={task.id}
                                  draggable
                                  onDragStart={() => handleDragStart(task.id)}
                                  onDragOver={handleDragOver}
                                  onDrop={() => handleDrop(template.id, task.id)}
                                  className="flex items-start gap-3 p-3 bg-muted/50 rounded-md cursor-move hover:bg-muted transition-colors"
                                  data-testid={`selected-task-${task.id}`}
                                >
                                  <GripVertical className="h-4 w-4 text-muted-foreground mt-0.5" />
                                  <span className="text-sm font-medium text-muted-foreground w-6">{index + 1}</span>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <p className="font-medium text-sm">{task.taskName}</p>
                                      <Badge variant="secondary" className="text-xs flex items-center gap-1">
                                        <Clock className="h-3 w-3" />
                                        {task.targetDuration}
                                      </Badge>
                                      {task.url && (
                                        <a 
                                          href={task.url} 
                                          target="_blank" 
                                          rel="noopener noreferrer" 
                                          className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                                          onClick={(e) => e.stopPropagation()}
                                          data-testid={`link-task-url-${task.id}`}
                                        >
                                          <Link className="h-3 w-3" />
                                          Reference
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleTaskSelection(template.id, task.id);
                                    }}
                                    data-testid={`button-remove-task-${task.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Available tasks */}
                        <div className="border-t pt-4">
                          <h4 className="text-sm font-medium mb-3">Available Tasks (select to add)</h4>
                          <div className="max-h-64 overflow-y-auto space-y-2 border rounded-md p-3 bg-background">
                            {allTaskTemplates.length === 0 ? (
                              <p className="text-sm text-muted-foreground italic">No task templates available</p>
                            ) : (
                              allTaskTemplates.map((task) => (
                                <label
                                  key={task.id}
                                  className="flex items-start gap-3 p-2 hover:bg-muted/50 rounded-md cursor-pointer transition-colors"
                                  data-testid={`available-task-${task.id}`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Checkbox
                                    checked={editing?.selectedTaskIds.includes(task.id) || false}
                                    onCheckedChange={() => toggleTaskSelection(template.id, task.id)}
                                    className="mt-0.5"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <p className="font-medium text-sm">{task.taskName}</p>
                                      <Badge variant="secondary" className="text-xs">
                                        {task.targetDuration}
                                      </Badge>
                                    </div>
                                    {task.taskDescription && (
                                      <p className="text-sm text-muted-foreground mt-1">
                                        {task.taskDescription}
                                      </p>
                                    )}
                                  </div>
                                </label>
                              ))
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              );
            })}

            {serviceTypesWithoutTemplates.length > 0 && (
              <Card className="border-dashed">
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground mb-3">
                    Service types without templates:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {serviceTypesWithoutTemplates.map((serviceType) => (
                      <Badge key={serviceType.id} variant="outline">
                        {serviceType.name}
                      </Badge>
                    ))}
                  </div>
                  <Button 
                    onClick={handleCreate} 
                    variant="outline" 
                    size="sm" 
                    className="mt-4"
                    data-testid="button-create-missing-template"
                  >
                    <Plus className="mr-2 h-3 w-3" />
                    Create Template
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        <ServiceTemplateFormModal
          open={modalOpen}
          onClose={handleCloseModal}
          template={selectedTemplate}
        />
      </div>
    </ResponsiveLayout>
  );
}
